import mixpanel from 'mixpanel-browser';

/**
 * Client-side Mixpanel for the outerweb signup funnel (GWB-402).
 *
 * Events fire from the browser so Mixpanel auto-captures the real client IP
 * (-> $country_code), $browser, $os, $device and $current_url, and stamps
 * mp_lib=web — none of which the queued server-side PHP tracking could provide.
 *
 * Applies the Cookiebot marketing-consent gate + gwbSrv injection and
 * exposes a small API the sign-up form calls to fire
 * signup:started / signup:completed. The token comes from
 * window.gwbSrv.mixpanel (ShowSignupPageFeature injection, gated on the
 * MIXPANEL_EVENTS flag), NOT a build-time var — so the single backend
 * MIXPANEL_TOKEN drives the correct project per environment
 * (Staging 4016274 / Live 4014873) without separate builds.
 *
 * Outerweb has no JS test infra, so this is verified manually on Staging
 * Live View (named exception to the worker app's vitest coverage).
 */

export const MixpanelEvent = {
  SIGNUP_STARTED: 'signup:started',
  SIGNUP_COMPLETED: 'signup:completed',
  // GWB-411: guest apply funnel (job detail page + marketing landing continue).
  GUEST_APPLICATION_STARTED: 'guest_application:started',
  GUEST_APPLICATION_SUBMITTED: 'guest_application:submitted',
  GUEST_APPLICATION_OTP_RESENT: 'guest_application:otp_resent',
  GUEST_APPLICATION_ACCOUNT_LINKED: 'guest_application:account_linked',
  // GWB-411: guest fire site on the public job list (/joboffers); the logged-in
  // fire sites live in the worker SPA plugin.
  WORKS_VIEWED: 'works:viewed',
};

const DEFAULT_API_HOST = 'https://api-eu.mixpanel.com';

// The initialized Mixpanel client, or null until consent + config are present.
let activeClient = null;

function normalizeApiHost(host) {
  if (!host) {
    return DEFAULT_API_HOST;
  }
  return /^https?:\/\//.test(host) ? host : `https://${host}`;
}

function hasMarketingConsent() {
  return Boolean(
    typeof window !== 'undefined'
      && window.Cookiebot
      && window.Cookiebot.consent
      && window.Cookiebot.consent.marketing,
  );
}

function init() {
  if (activeClient) {
    return;
  }

  const { gwbSrv } = window;
  const cfg = gwbSrv && gwbSrv.mixpanel;
  if (!cfg || !cfg.token || !hasMarketingConsent()) {
    return;
  }

  mixpanel.init(cfg.token, {
    api_host: normalizeApiHost(cfg.host),
    autocapture: false,
    persistence: 'localStorage',
  });

  // user_language is a required prop on signup:started; register once so it
  // auto-attaches to every event.
  if (cfg.user_language) {
    mixpanel.register({ user_language: cfg.user_language });
  }

  activeClient = mixpanel;
}

// `options` is forwarded to the Mixpanel SDK — e.g. { transport: 'sendBeacon' } so an
// event survives an immediate page navigation (the OAuth signup:started anchors).
export function track(event, props = {}, options = undefined) {
  if (!activeClient) {
    return false;
  }
  activeClient.track(event, props, options);
  return true;
}

export function setUserProperties(props = {}) {
  if (!activeClient) {
    return false;
  }
  activeClient.people.set(props);
  return true;
}

// signup:started fires anonymously (no account yet); identify() on completion
// merges the anonymous device id into the user profile — correct funnel behaviour.
export function identify(userId) {
  if (!activeClient || userId == null) {
    return false;
  }
  activeClient.identify(String(userId));
  return true;
}

if (typeof window !== 'undefined') {
  if (hasMarketingConsent()) {
    init();
  } else {
    window.addEventListener('CookiebotOnConsentReady', init);
  }
}
